/**
 * Iter 35b — Two-tab UI helpers for AnalysisResults.
 *
 * The user's Iter 35 directive is:
 *   • Consultancy View is the protected default tab (renders the legacy
 *     consultancy layout — composite chips, weighted dimensions,
 *     score drivers, executive summary).
 *   • Marketplace Opportunity is a SECOND tab that appears only when
 *     the backend emits the additive `marketplace_opportunity` block
 *     (Iter 35a). It is never the default.
 *   • Marketplace tab content is sourced exclusively from
 *     `analysis.marketplace_opportunity` — the Iter 34 keys
 *     (`opportunity_mode`, `purchase_readiness`, `supply_readiness`)
 *     stay in the response and remain a backward-compatibility
 *     surface, but the tab does not consume them.
 *
 * Pure-function helpers — no React, no DOM. The component imports
 * them; the unit tests assert their contract.
 */

export const ANALYSIS_TABS = {
  CONSULTANCY: 'consultancy',
  MARKETPLACE: 'marketplace',
};

export const DEFAULT_ANALYSIS_TAB = ANALYSIS_TABS.CONSULTANCY;

/**
 * Return the additive marketplace block, or null when absent / invalid.
 * Iter 35b reads ONLY this key — never the Iter 34 readiness blocks —
 * so the Marketplace tab can be retired or evolved independently of
 * the legacy mode-aware keys.
 */
export function getMarketplaceBlock(analysis) {
  if (!analysis || typeof analysis !== 'object') return null;
  const block = analysis.marketplace_opportunity;
  if (!block || typeof block !== 'object') return null;
  return block;
}

/**
 * The Marketplace Opportunity tab renders only when the backend
 * attached a valid additive block. Returns false otherwise so the
 * tab bar itself is suppressed and the Consultancy View renders
 * full-bleed (matches pre-Iter-35 layout for every legacy sector).
 */
export function hasMarketplaceTab(analysis) {
  return getMarketplaceBlock(analysis) !== null;
}

/**
 * Normalise the readiness_mode label coming back from the backend
 * into an operator-facing pill. Defensive against unknown values —
 * if the backend rolls out a new readiness mode before the UI knows
 * about it, the pill simply renders the raw label rather than
 * silently hiding it.
 */
export function readinessPillLabel(readinessMode) {
  if (!readinessMode || typeof readinessMode !== 'string') return null;
  if (readinessMode === 'purchase_readiness') return 'Purchase readiness';
  if (readinessMode === 'supply_readiness') return 'Supply readiness';
  return readinessMode.replace(/_/g, ' ');
}

/**
 * Operator-facing display for a snake_case dimension key.
 * "kit_or_equipment_need" → "Kit Or Equipment Need".
 */
export function humaniseDimensionKey(key) {
  if (!key || typeof key !== 'string') return '';
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Operator-facing display for the marketplace recommendation band.
 * "engage_now" → "ENGAGE NOW".
 */
export function formatMarketplaceRecommendation(rec) {
  return (rec || '').replace(/_/g, ' ').toUpperCase();
}


// ─── Iter 35d — Fit Marketplace Opportunity helpers ────────────────

/**
 * Pull the Iter-35a fit pack off a Fit response. The fit pack is
 * the operator-facing marketplace fit block: approach shape,
 * score, recommendation, contactability, first move, urgency,
 * buy-vs-hire indicators (Construction) or need-visible drivers
 * (Sport). Returns null when the marketplace block or fit_pack is
 * missing.
 *
 * Iter 35d reads ONLY this nested key — never the legacy Iter 34
 * `purchase_fit` / `supply_fit` / `engagement_fit` keys — so the
 * Marketplace tab can evolve independently.
 */
export function getMarketplaceFitPack(fit) {
  const block = getMarketplaceBlock(fit);
  if (!block) return null;
  const fp = block.fit_pack;
  if (!fp || typeof fp !== 'object') return null;
  return fp;
}

/**
 * The Marketplace Opportunity tab on the Fit screen renders only
 * when both the marketplace block and its `fit_pack` are present
 * AND the fit_pack has *some* renderable content — at least one
 * of: approach_shape, score, recommendation, first_move,
 * need_visible, product_angle, evidence_summary. Prevents the
 * tab from rendering empty when the readiness scorer produced
 * no usable fit guidance.
 */
export function hasMarketplaceFitTab(fit) {
  const fp = getMarketplaceFitPack(fit);
  if (!fp) return false;
  if (fp.approach_shape || fp.product_angle || fp.first_move
      || fp.evidence_summary || fp.recommendation
      || fp.verdict || fp.best_fit_role_label) return true;
  if (typeof fp.score === 'number') return true;
  if (Array.isArray(fp.need_visible) && fp.need_visible.length > 0) return true;
  if (Array.isArray(fp.fit_drivers) && fp.fit_drivers.length > 0) return true;
  return false;
}

/**
 * Unified shape consumed by the Marketplace tab on the Fit screen.
 * Returns null when the tab should not render.
 *
 * Field mapping (Marketplace Fit panel):
 *   * Marketplace Opportunity Verdict ← fit_pack.recommendation ?? block.recommendation
 *   * Best-Fit Marketplace Role(s)    ← block.roles
 *   * Fit Drivers                     ← fit_pack.need_visible (humanised)
 *   * Commercial Value to Marketplace ← fit_pack.product_angle ?? fit_pack.approach_shape
 *                                       ?? block.approach_shape
 *   * Recommended Next Action         ← fit_pack.first_move
 *   * Evidence Strength               ← fit_pack.evidence_summary ?? block.evidence_summary
 *   * Score                           ← fit_pack.score ?? block.score
 *   * Risks / Watch-outs              ← block.disqualifiers
 *   * Contactability                  ← fit_pack.contactability (Sport / Construction shared shape)
 *   * Urgency (Sport)                 ← fit_pack.urgency
 *   * Procurement urgency (Construction) ← fit_pack.procurement_urgency
 *   * Buy vs Hire (Construction)      ← fit_pack.buy_vs_hire
 */
export function getMarketplaceFitContent(fit) {
  if (!hasMarketplaceFitTab(fit)) return null;
  const block = getMarketplaceBlock(fit) || {};
  const fp = block.fit_pack || {};
  const need = Array.isArray(fp.need_visible) ? fp.need_visible : [];
  // Iter 36f-2 — Sport readiness now emits a humanised `fit_drivers`
  // list directly (no need to humanise dimension keys). Fall back to
  // the Iter-34 `need_visible` shape for Construction / legacy data.
  const fitDriversFromBackend = Array.isArray(fp.fit_drivers) ? fp.fit_drivers : null;
  const driversDisplay = fitDriversFromBackend
    ? fitDriversFromBackend.slice()
    : need.map((k) => humaniseDimensionKey(k));
  // Iter 36f-2 — `contactability` may be a scalar score on Sport
  // (Iter 36f-2 contract) or an {score, contact_url} object on
  // Construction (Iter 34/35). Coerce to the object shape for the
  // UI so the existing ContactabilityPanel renders both.
  let contactability = null;
  if (fp.contactability && typeof fp.contactability === 'object') {
    contactability = fp.contactability;
  } else if (typeof fp.contactability === 'number') {
    contactability = { score: fp.contactability };
  }
  // Iter 36f-2 — Sport verdict is a sentence; Construction
  // verdict is a recommendation band token. Prefer the Sport
  // sentence when present.
  return {
    verdict:               fp.verdict || fp.recommendation || block.recommendation || null,
    score:                 typeof fp.score === 'number'
                             ? fp.score
                             : (typeof block.score === 'number' ? block.score : null),
    angle:                 fp.commercial_or_community_value
                             || fp.product_angle
                             || fp.approach_shape
                             || block.approach_shape
                             || null,
    fit_drivers:           driversDisplay,
    fit_drivers_raw:       fitDriversFromBackend || need,
    first_action:          fp.recommended_next_action || fp.first_move || null,
    evidence_strength:     typeof fp.evidence_summary === 'string'
                             ? fp.evidence_summary
                             : (block.evidence_summary || null),
    roles:                 Array.isArray(block.roles) ? block.roles : [],
    watchouts:             Array.isArray(fp.watchouts) && fp.watchouts.length > 0
                             ? fp.watchouts
                             : (Array.isArray(block.disqualifiers) ? block.disqualifiers : []),
    contactability,
    urgency:               typeof fp.urgency === 'number' ? fp.urgency : null,
    procurement_urgency:   (fp.procurement_urgency && typeof fp.procurement_urgency === 'object')
                             ? fp.procurement_urgency
                             : null,
    buy_vs_hire:           typeof fp.buy_vs_hire === 'number' ? fp.buy_vs_hire : null,
    readiness_mode:        block.readiness_mode || null,
    sector:                block.sector || null,
    // Iter 36f-2 additive fields (omitted on legacy payloads).
    best_fit_role:         fp.best_fit_role || block.best_fit_role || null,
    best_fit_role_label:   fp.best_fit_role_label || block.best_fit_role_label || null,
    role_scores:           (fp.marketplace_role_scores && typeof fp.marketplace_role_scores === 'object')
                             ? fp.marketplace_role_scores
                             : (block.marketplace_role_scores || null),
    role_explanation:      Array.isArray(fp.marketplace_role_explanation)
                             ? fp.marketplace_role_explanation
                             : (Array.isArray(block.marketplace_role_explanation)
                                  ? block.marketplace_role_explanation : []),
    role_watchouts:        Array.isArray(fp.marketplace_role_watchouts)
                             ? fp.marketplace_role_watchouts
                             : (Array.isArray(block.marketplace_role_watchouts)
                                  ? block.marketplace_role_watchouts : []),
    evidence_to_validate:  Array.isArray(fp.evidence_to_validate)
                             ? fp.evidence_to_validate
                             : (Array.isArray(block.evidence_to_validate)
                                  ? block.evidence_to_validate : []),
  };
}

// ─── Iter 35c — Fix Marketplace Activation Plan helpers ────────────

/**
 * Pull the Iter-35a fix pack off a Fix response. The fix pack is
 * the operator-facing activation plan: title, approach shape,
 * suggested first move, grouped activation steps, recommendation.
 * Returns null when the marketplace block or fix_pack is missing.
 *
 * Iter 35c reads ONLY this nested key — never the legacy Iter 34
 * `opportunity_plan` / `purchase_readiness` / `supply_readiness`
 * keys — so the Marketplace tab can evolve independently.
 */
export function getMarketplaceFixPack(roadmap) {
  const block = getMarketplaceBlock(roadmap);
  if (!block) return null;
  const fp = block.fix_pack;
  if (!fp || typeof fp !== 'object') return null;
  return fp;
}

/**
 * The Marketplace Opportunity tab on the Fix screen renders only
 * when both the marketplace block and its `fix_pack` are present
 * AND the fix_pack has *some* renderable activation content (a
 * `pack_title`, an `approach_shape`, a `suggested_first_move`, or
 * at least one non-empty group). This prevents an empty-tab UX
 * where the backend attached the wrapper but the readiness scorer
 * produced no usable activation guidance.
 */
export function hasMarketplaceFixTab(roadmap) {
  const fp = getMarketplaceFixPack(roadmap);
  if (!fp) return false;
  if (fp.pack_title || fp.approach_shape || fp.suggested_first_move
      || fp.first_conversation || fp.best_fit_role_label) return true;
  if (Array.isArray(fp.groups)
      && fp.groups.some((g) => Array.isArray(g.items) && g.items.length > 0)) {
    return true;
  }
  if (Array.isArray(fp.activation_steps) && fp.activation_steps.length > 0) {
    return true;
  }
  return false;
}

/**
 * Unified shape consumed by the Marketplace tab. Returns null when
 * the tab should not render. The Marketplace tab's display fields
 * mapped from the backend payload:
 *
 *   * Marketplace Activation Plan title    ← fix_pack.pack_title (fallback "Marketplace Activation Plan")
 *   * Recommended Marketplace Angle        ← fix_pack.approach_shape
 *   * Why This Angle Fits                  ← marketplace.evidence_summary
 *   * Suggested First Conversation         ← fix_pack.suggested_first_move
 *   * Activation Steps                     ← fix_pack.groups
 *   * Marketplace Offer Hook               ← first non-empty group's first item.action,
 *                                            fallback fix_pack.approach_shape
 *   * Risks / Watchouts                    ← marketplace.disqualifiers
 *   * Evidence Strength                    ← marketplace.evidence_summary
 *   * Marketplace Role chips               ← marketplace.roles
 *
 * Sibling fields read from the *parent* marketplace_opportunity
 * block — also Iter 35a, NOT Iter 34 — so the contract stays
 * inside the new payload surface.
 */
export function getMarketplaceFixContent(roadmap) {
  if (!hasMarketplaceFixTab(roadmap)) return null;
  const block = getMarketplaceBlock(roadmap) || {};
  const fp = block.fix_pack || {};
  // Iter 36f-2 — `groups` is the preferred shape (role-aware
  // grouped workstreams). When absent, synthesise a single
  // "Activation Steps" group from the legacy flat
  // `activation_steps` so Iter-34 / Iter-35 callers still render.
  let groups = Array.isArray(fp.groups)
    ? fp.groups.filter((g) => Array.isArray(g.items) && g.items.length > 0)
    : [];
  if (groups.length === 0 && Array.isArray(fp.activation_steps) && fp.activation_steps.length > 0) {
    groups = [{
      title: 'Activation Steps',
      items: fp.activation_steps.map((s) => ({
        action: s.step || s.action || '',
        metric: s.metric || '',
        risk:   s.risk || '',
      })).filter((i) => i.action),
    }];
  }
  const firstHook = groups[0] && groups[0].items[0] && groups[0].items[0].action;
  return {
    title:                fp.pack_title || 'Marketplace Activation Plan',
    angle:                fp.approach_shape || block.approach_shape || null,
    why_this_fits:        fp.why_this_angle_fits || block.evidence_summary || null,
    first_conversation:   fp.suggested_first_move || fp.first_conversation || null,
    groups,
    offer_hook:           fp.offer_hook || firstHook || fp.approach_shape || block.approach_shape || null,
    watchouts:            Array.isArray(fp.watchouts) && fp.watchouts.length > 0
                            ? fp.watchouts
                            : (Array.isArray(block.disqualifiers) ? block.disqualifiers : []),
    evidence_strength:    block.evidence_summary || null,
    roles:                Array.isArray(block.roles) ? block.roles : [],
    recommendation:       fp.recommendation || block.recommendation || null,
    composite_score:      typeof fp.composite_score === 'number'
                            ? fp.composite_score
                            : (typeof fp.score === 'number'
                                ? fp.score
                                : (typeof block.score === 'number' ? block.score : null)),
    readiness_mode:       block.readiness_mode || null,
    sector:               block.sector || null,
    // Iter 36f-2 additive fields.
    best_fit_role:        fp.best_fit_role || block.best_fit_role || null,
    best_fit_role_label:  fp.best_fit_role_label || block.best_fit_role_label || null,
    role_scores:          (fp.marketplace_role_scores && typeof fp.marketplace_role_scores === 'object')
                            ? fp.marketplace_role_scores
                            : (block.marketplace_role_scores || null),
    role_explanation:     Array.isArray(fp.marketplace_role_explanation)
                            ? fp.marketplace_role_explanation
                            : (Array.isArray(block.marketplace_role_explanation)
                                ? block.marketplace_role_explanation : []),
    role_watchouts:       Array.isArray(fp.marketplace_role_watchouts)
                            ? fp.marketplace_role_watchouts
                            : (Array.isArray(block.marketplace_role_watchouts)
                                ? block.marketplace_role_watchouts : []),
    evidence_to_validate: Array.isArray(fp.evidence_to_validate)
                            ? fp.evidence_to_validate
                            : (Array.isArray(block.evidence_to_validate)
                                ? block.evidence_to_validate : []),
  };
}
